import React from "react";
import { Button, Card, Form, Input, Select, Space, Typography } from "antd";
import type { ScreenMap } from "antd/es/_util/responsiveObserver";
import { PlusOutlined } from "@ant-design/icons";
import { buildStyleOptions, buildVisibilityOptions } from "./constants";
import { TeamForm } from "./types";
import { TournamentStyle, TournamentVisibility } from "../../../generated/bpong_pb";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

type TournamentFormProps = {
    tournamentName: string;
    selectedStyle: TournamentStyle;
    selectedVisibility: TournamentVisibility;
    accessPassword: string;
    teams: TeamForm[];
    creatingTournament: boolean;
    onTournamentNameChange: (value: string) => void;
    onStyleChange: (style: TournamentStyle) => void;
    onVisibilityChange: (visibility: TournamentVisibility) => void;
    onAccessPasswordChange: (value: string) => void;
    onAddTeam: () => void;
    onRemoveTeam: (id: number) => void;
    onUpdateTeamName: (id: number, name: string) => void;
    onAddParticipant: (teamId: number) => void;
    onUpdateParticipant: (teamId: number, index: number, value: string) => void;
    onRemoveParticipant: (teamId: number, index: number) => void;
    onSubmit: () => void;
    screens: ScreenMap;
};

const TournamentForm: React.FC<TournamentFormProps> = ({
    tournamentName,
    selectedStyle,
    selectedVisibility,
    accessPassword,
    teams,
    creatingTournament,
    onTournamentNameChange,
    onStyleChange,
    onVisibilityChange,
    onAccessPasswordChange,
    onAddTeam,
    onRemoveTeam,
    onUpdateTeamName,
    onAddParticipant,
    onUpdateParticipant,
    onRemoveParticipant,
    onSubmit,
    screens,
}) => {
    const requiresPassword =
        selectedVisibility === TournamentVisibility.TOURNAMENT_VISIBILITY_PASSWORD;
    const { t } = useTranslation();
    const styleOptions = buildStyleOptions(t);
    const visibilityOptions = buildVisibilityOptions(t);

    return (
        <Card
        title={t("beerpong.tournament.form.title")}
        size="small"
        style={{ background: "transparent" }}
        styles={{ body: { paddingBottom: 16 } }}
    >
        <Form layout="vertical" component={false} colon={false}>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Form.Item label={t("beerpong.tournament.form.nameLabel")} htmlFor="bp-tournament-name" style={{ marginBottom: 0 }}>
                <Input
                    id="bp-tournament-name"
                    value={tournamentName}
                    maxLength={80}
                    onChange={(event) => onTournamentNameChange(event.target.value)}
                />
            </Form.Item>
            <Form.Item label={t("beerpong.tournament.form.styleLabel")} htmlFor="bp-tournament-style" style={{ marginBottom: 0 }}>
                <Select<TournamentStyle>
                    id="bp-tournament-style"
                    value={selectedStyle}
                    onChange={onStyleChange}
                    options={styleOptions}
                    style={{ width: "100%", maxWidth: screens.xs ? "100%" : 260 }}
                />
            </Form.Item>
            <Form.Item label={t("beerpong.tournament.form.visibilityLabel")} htmlFor="bp-tournament-visibility" style={{ marginBottom: 0 }}>
                <Select<TournamentVisibility>
                    id="bp-tournament-visibility"
                    value={selectedVisibility}
                    onChange={onVisibilityChange}
                    options={visibilityOptions}
                    style={{ width: "100%", maxWidth: screens.xs ? "100%" : 260 }}
                />
            </Form.Item>
            {requiresPassword && (
                <Form.Item label={t("beerpong.tournament.form.passwordLabel")} htmlFor="bp-tournament-password" style={{ marginBottom: 0 }}>
                    <Input.Password
                        id="bp-tournament-password"
                        maxLength={64}
                        value={accessPassword}
                        onChange={(event) => onAccessPasswordChange(event.target.value)}
                    />
                </Form.Item>
            )}
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
                {teams.map((team, index) => (
                    <Card
                        key={team.id}
                        size="small"
                        type="inner"
                        title={t("beerpong.tournament.form.teamTitle", { number: index + 1 })}
                        extra={
                            teams.length > 2 ? (
                                <Button type="link" danger onClick={() => onRemoveTeam(team.id)}>
                                    {t("beerpong.tournament.form.removeTeam")}
                                </Button>
                            ) : null
                        }
                    >
                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Form.Item label={t("beerpong.tournament.form.teamNameLabel")} htmlFor={`bp-team-name-${team.id}`} style={{ marginBottom: 0 }}>
                                <Input
                                    id={`bp-team-name-${team.id}`}
                                    value={team.name}
                                    maxLength={60}
                                    onChange={(event) => onUpdateTeamName(team.id, event.target.value)}
                                />
                            </Form.Item>
                            <Space direction="vertical" style={{ width: "100%" }} size="small">
                                {team.participants.map((participant, participantIndex) => (
                                    <Space
                                        key={`${team.id}-${participantIndex}`}
                                        style={{
                                            width: "100%",
                                            flexWrap: "wrap",
                                            gap: 8,
                                            justifyContent: "space-between",
                                        }}
                                        align="center"
                                    >
                                        <Form.Item
                                            label={t("beerpong.tournament.form.participantLabel", { number: participantIndex + 1 })}
                                            htmlFor={`bp-participant-${team.id}-${participantIndex}`}
                                            style={{ flex: 1, minWidth: screens.xs ? "100%" : 220, marginBottom: 0 }}
                                        >
                                            <Input
                                                id={`bp-participant-${team.id}-${participantIndex}`}
                                                value={participant}
                                                maxLength={60}
                                                onChange={(event) =>
                                                    onUpdateParticipant(team.id, participantIndex, event.target.value)
                                                }
                                            />
                                        </Form.Item>
                                        <Button
                                            type="text"
                                            danger
                                            disabled={team.participants.length <= 1}
                                            onClick={() => onRemoveParticipant(team.id, participantIndex)}
                                        >
                                            {t("beerpong.tournament.form.removeParticipant")}
                                        </Button>
                                    </Space>
                                ))}
                            </Space>
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={() => onAddParticipant(team.id)}
                                block={screens.xs}
                            >
                                {t("beerpong.tournament.form.addParticipant")}
                            </Button>
                        </Space>
                    </Card>
                ))}
            </Space>
            <Space style={{ width: "100%", flexWrap: "wrap", gap: 12 }}>
                <Button type="dashed" icon={<PlusOutlined />} onClick={onAddTeam} block={screens.xs}>
                    {t("beerpong.tournament.form.addTeam")}
                </Button>
                <Button type="primary" onClick={onSubmit} loading={creatingTournament} block={screens.xs}>
                    {t("beerpong.tournament.form.submit")}
                </Button>
            </Space>
            {teams.length < 2 && (
                <Text type="secondary">{t("beerpong.tournament.form.hintMinTeams")}</Text>
            )}
        </Space>
        </Form>
    </Card>
    );
};

export default TournamentForm;
